import {
  useEffect,
  useLayoutEffect,
  useId,
  useImperativeHandle,
  useRef,
  type ReactNode,
  type Ref,
} from 'react';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableFooter } from './Table';

export interface WideColumn<T> {
  id: string;
  title: string;
  numeric?: boolean;
  render: (row: T) => ReactNode;
  total?: ReactNode;
}
export interface WidePendingColumn {
  id: string;
  title?: string;
  numeric?: boolean;
}
const PENDING: WidePendingColumn[] = [
  { id: 'next-1', numeric: true },
  { id: 'next-2', numeric: true },
];

/** Owner supplies columns, totals, loading and persistence. Existing Table defaults stay unchanged. */
export function WideTable<T>({
  rows,
  rowKey,
  rowLabel,
  columns,
  loading = false,
  error,
  hasMore = false,
  onLoadMore,
  automatic = false,
  pendingColumns = PENDING,
  viewportRef,
  label = 'Широкая таблица',
  rowHeading = 'Запись',
  totalLabel = 'Итого',
}: {
  rows: readonly T[];
  rowKey: (row: T) => string;
  rowLabel: (row: T) => ReactNode;
  columns: readonly WideColumn<T>[];
  loading?: boolean;
  error?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  automatic?: boolean;
  pendingColumns?: readonly WidePendingColumn[];
  viewportRef?: Ref<HTMLDivElement>;
  label?: string;
  rowHeading?: string;
  totalLabel?: string;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const totalsViewport = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = viewport.current;
    const totals = totalsViewport.current;
    if (!el || !totals) return;
    const sync = () => {
      // Include the native vertical scrollbar gutter in the reserved totals strip.
      totals.style.width = el.clientWidth + 'px';
      totals.scrollLeft = el.scrollLeft;
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [columns, loading, error, rows.length]);
  useImperativeHandle(viewportRef, () => viewport.current!);
  const help = useId();
  const requested = useRef(false);
  const lastLeft = useRef(0);
  useEffect(() => {
    requested.current = false;
  }, [loading, error, columns]);
  const pending = loading || error ? pendingColumns : [];
  const load = () => {
    if (loading || requested.current || !onLoadMore || (!hasMore && !error)) return;
    requested.current = true;
    onLoadMore();
  };
  return (
    <div
      data-slot="wide-table"
      className="@container/wide min-w-0 overflow-hidden rounded-lg border border-line bg-card"
    >
      <div className="flex h-96 flex-col">
        <Table
          stickyFirst
          viewportRef={viewport}
          viewportClassName="min-h-0 flex-1 overflow-auto scroll-pt-9 scroll-pb-10"
          viewportProps={{
            tabIndex: 0,
            role: 'region',
            'aria-label': label,
            'aria-describedby': help,
            onKeyDown: (event) => {
              if (event.target !== event.currentTarget || event.altKey) return;
              const el = event.currentTarget;
              let left = el.scrollLeft,
                top = el.scrollTop;
              const control = event.ctrlKey || event.metaKey;
              if (control && !['Home', 'End'].includes(event.key)) return;
              if (event.key === 'ArrowRight') left += 160;
              else if (event.key === 'ArrowLeft') left -= 160;
              else if (event.key === 'ArrowDown') top += 40;
              else if (event.key === 'ArrowUp') top -= 40;
              else if (event.key === 'PageDown') top += Math.max(40, el.clientHeight - 36);
              else if (event.key === 'PageUp') top -= Math.max(40, el.clientHeight - 36);
              else if (event.key === 'Home') {
                left = 0;
                if (control) top = 0;
              } else if (event.key === 'End') {
                left = el.scrollWidth;
                if (control) top = el.scrollHeight;
              } else return;
              event.preventDefault();
              el.scrollTo({ left, top, behavior: 'instant' });
            },
            onScroll: (event) => {
              const el = event.currentTarget;
              if (totalsViewport.current) totalsViewport.current.scrollLeft = el.scrollLeft;
              const forward = el.scrollLeft > lastLeft.current;
              lastLeft.current = el.scrollLeft;
              if (
                automatic &&
                forward &&
                el.clientWidth > 0 &&
                el.clientHeight > 0 &&
                hasMore &&
                !loading &&
                !error &&
                el.scrollWidth - el.clientWidth - el.scrollLeft < 160
              )
                load();
            },
          }}
          footer={
            !rows.length && (
              <EmptyState
                title="Записей пока нет"
                description="Когда появятся данные, они займут строки этой таблицы."
                className="sticky left-0 rounded-none border-0"
              />
            )
          }
          className="w-max table-fixed border-separate border-spacing-0"
          aria-label={label}
          aria-busy={loading}
        >
          <colgroup>
            <col className="w-24 @md/wide:w-60" />
            {columns.map((c) => (
              <col key={c.id} className="w-40" />
            ))}
            {pending.map((c) => (
              <col key={'pending-' + c.id} className="w-40" />
            ))}
          </colgroup>
          <TableHeader className="z-dock">
            <TableRow>
              <TableHead scope="col" className="z-raised w-24 text-fg-muted @md/wide:w-60">
                {rowHeading}
              </TableHead>
              {columns.map((column) => (
                <TableHead
                  data-wide-column={column.id}
                  scope="col"
                  numeric={column.numeric}
                  key={column.id}
                  className="w-40 text-fg-muted"
                >
                  {column.title}
                </TableHead>
              ))}
              {pending.map((column) => (
                <TableHead
                  scope="col"
                  key={column.id}
                  data-column-pending={column.id}
                  aria-label={
                    (loading ? 'Загружается' : 'Недоступна') +
                    (column.title ? ': ' + column.title : ' колонка')
                  }
                  className="w-40"
                >
                  <Skeleton still={Boolean(error)} className={column.numeric ? 'ml-auto w-20' : 'w-20'} />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const name = rowLabel(row);
              return (
                <TableRow key={rowKey(row)} data-wide-row={rowKey(row)}>
                  <TableHead
                    scope="row"
                    className="z-raised h-10 w-24 text-sm font-medium tracking-normal text-fg normal-case @md/wide:w-60"
                  >
                    <div
                      className="w-16 truncate @md/wide:w-48"
                      title={typeof name === 'string' ? name : undefined}
                    >
                      {name}
                    </div>
                  </TableHead>
                  {columns.map((column) => (
                    <TableCell
                      data-wide-cell={column.id}
                      key={column.id}
                      numeric={column.numeric}
                      className="w-40 font-sans text-sm whitespace-nowrap"
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {pending.map((column) => (
                    <TableCell
                      data-column-pending={column.id}
                      key={column.id}
                      className="w-40"
                      aria-label={loading ? 'Данные загружаются' : 'Данные недоступны'}
                    >
                      <Skeleton still={Boolean(error)} className={column.numeric ? 'ml-auto w-24' : 'w-24'} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {rows.length > 0 && (
          <div data-wide-totals className="h-10 shrink-0 overflow-hidden bg-card">
            <Table
              stickyFirst
              viewportRef={totalsViewport}
              viewportClassName="h-10 overflow-hidden"
              className="w-max table-fixed border-separate border-spacing-0"
              aria-label={label + ' — итоги'}
              aria-busy={loading}
            >
              <colgroup>
                <col className="w-24 @md/wide:w-60" />
                {columns.map((c) => (
                  <col key={c.id} className="w-40" />
                ))}
                {pending.map((c) => (
                  <col key={'pending-' + c.id} className="w-40" />
                ))}
              </colgroup>
              <TableFooter className="z-dock">
                <TableRow>
                  <TableHead
                    scope="row"
                    className="z-raised h-10 w-24 text-sm font-semibold tracking-normal text-fg normal-case @md/wide:w-60"
                  >
                    {totalLabel}
                  </TableHead>
                  {columns.map((column) => (
                    <TableCell
                      data-wide-total={column.id}
                      key={column.id}
                      numeric={column.numeric}
                      className="w-40 font-sans text-sm"
                    >
                      <span className="sr-only">{column.title}: </span>
                      {column.total ?? '—'}
                    </TableCell>
                  ))}
                  {pending.map((column) => (
                    <TableCell data-column-pending={column.id} key={column.id} className="w-40">
                      <Skeleton still={Boolean(error)} className={column.numeric ? 'ml-auto w-24' : 'w-24'} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </div>
      <div className="flex flex-col items-stretch gap-3 border-t border-line px-4 py-3 @md/wide:flex-row @md/wide:items-center @md/wide:justify-between">
        <div className="min-w-0 flex-1">
          <p
            role={error ? 'alert' : 'status'}
            aria-live={error ? 'assertive' : 'polite'}
            className={error ? 'text-xs text-danger-strong' : 'text-xs text-fg-muted'}
          >
            {error ??
              (loading
                ? 'Добавляем колонки: ' + pending.length
                : hasMore
                  ? 'Колонок показано: ' + columns.length + '. Следующие — справа.'
                  : 'Все колонки загружены: ' + columns.length)}
          </p>
          <p id={help} className="mt-1 text-xs text-fg-muted">
            Tab — перейти к таблице. Стрелки — прокрутить, Home / End — к краям, Page Up / Down — по строкам.
          </p>
        </div>
        {onLoadMore && (
          <Button
            variant="secondary"
            size="sm"
            aria-disabled={loading || (!hasMore && !error)}
            className="self-start aria-disabled:cursor-default aria-disabled:opacity-50 @md/wide:self-auto"
            onClick={load}
          >
            {error ? 'Повторить' : loading ? 'Загружаем колонки…' : hasMore ? 'Ещё колонки' : 'Все колонки'}
          </Button>
        )}
      </div>
    </div>
  );
}
